import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Lock } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";
import type { Connection } from "@zworks/console-contracts";
import { NotConnectedState } from "@/components/states";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { ApiError, attachConnectionTool, connectionsQuery } from "@/lib/api";

/** The statuses from which a tool may be attached: the hub refuses every other state. */
const usableStatuses: Connection["status"][] = ["active", "degraded"];

/** Matches the server's derivation: destructive names first, then write, and unknown means write. */
function scopeClass(scope: string): "read" | "write" | "destructive" {
  const name = scope.toLowerCase();
  if (/delet|destr|\bdrop\b|destroy|revoke|terminat|purge|wipe/.test(name)) return "destructive";
  if (/write|create|update|merge|push|publish|comment|post|send|attach/.test(name)) return "write";
  if (/read|select|list|view|\bget\b/.test(name)) return "read";
  return "write";
}

const riskLabels: Record<
  "read" | "write" | "destructive",
  { label: string; variant: "success" | "warning" | "danger" }
> = {
  read: { label: "Low", variant: "success" },
  write: { label: "Medium", variant: "warning" },
  destructive: { label: "High", variant: "danger" },
};

interface Props {
  projectId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAdded: (toolName: string) => void;
}

/** Adds a tool from an existing connection. Write actions are off by default; the server decides. */
export function AddToolFromIntegrationDialog({ projectId, open, onOpenChange, onAdded }: Props) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby="tool-integration-description">
        <DialogTitle>Add a tool from an integration</DialogTitle>
        <DialogDescription id="tool-integration-description">
          Pick a connection, then the actions you allow.
        </DialogDescription>
        {/* Radix unmounts the content when the dialog closes, so the form starts fresh each time. */}
        <IntegrationToolForm
          projectId={projectId}
          onAdded={onAdded}
          onDone={() => onOpenChange(false)}
        />
      </DialogContent>
    </Dialog>
  );
}

function IntegrationToolForm({
  projectId,
  onAdded,
  onDone,
}: {
  projectId: string;
  onAdded: (toolName: string) => void;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const connections = useQuery(connectionsQuery(projectId));
  const usable = (connections.data ?? []).filter((row) => usableStatuses.includes(row.status));
  const [connectionId, setConnectionId] = useState("");
  const [toolName, setToolName] = useState("");
  const [allowed, setAllowed] = useState<Record<string, boolean>>({});
  const [error, setError] = useState<string | null>(null);

  const selected = usable.find((row) => row.id === connectionId) ?? null;
  const scopes = selected?.scopes ?? [];
  const chosen = scopes.filter((scope) => allowed[scope]);
  const writeChosen = chosen.some((scope) => scopeClass(scope) !== "read");
  const riskClass = chosen.some((scope) => scopeClass(scope) === "destructive")
    ? "destructive"
    : writeChosen
      ? "write"
      : "read";

  // Read actions start on and write actions start off, so no write is granted by default.
  const choose = (row: Connection) => {
    setConnectionId(row.id);
    setToolName(row.name);
    setAllowed(
      Object.fromEntries(row.scopes.map((scope) => [scope, scopeClass(scope) === "read"])),
    );
  };

  const mutation = useMutation({
    mutationFn: () =>
      attachConnectionTool(projectId, connectionId, {
        tool: toolName.trim(),
        scopes: chosen,
        allowWrite: writeChosen,
      }),
    onSuccess: async (binding) => {
      await queryClient.invalidateQueries({ queryKey: ["projects", projectId, "tools"] });
      onAdded(binding.tool);
      onDone();
    },
    onError: (failure) => {
      if (failure instanceof ApiError && failure.status === 422) {
        setError(
          "The connection does not allow the chosen actions. Write actions are off by default.",
        );
      } else if (failure instanceof ApiError && failure.status === 409) {
        setError(
          "This tool is already attached with other actions, or the connection is not active.",
        );
      } else {
        setError("The tool could not be added. Check the details and try again.");
      }
    },
  });

  const canSubmit =
    selected !== null && toolName.trim().length > 0 && chosen.length > 0 && !mutation.isPending;

  return (
    <>
      {connections.isPending ? (
        <p role="status" className="mt-4 text-sm text-muted-foreground">
          Loading connections
        </p>
      ) : connections.isError ? (
        <div className="mt-4">
          <NotConnectedState
            title="Connections are not available"
            description="Connections are not switched on for this console. Connect an integration first."
          />
        </div>
      ) : usable.length === 0 ? (
        <div className="mt-4">
          <NotConnectedState
            title="No active connection"
            description="Connect an integration and approve it. Its actions can then become tools."
          />
          <p className="mt-3 text-sm">
            <Link className="underline underline-offset-4" to="/integrations">
              Open Integrations
            </Link>
          </p>
        </div>
      ) : (
        <form
          className="mt-4 flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (canSubmit) mutation.mutate();
          }}
        >
          <label className="grid gap-1 text-sm">
            Integration
            <select
              aria-label="Integration"
              className="h-11 rounded-md border bg-background px-3 text-sm md:h-9"
              value={connectionId}
              onChange={(event) => {
                const row = usable.find((candidate) => candidate.id === event.target.value);
                if (row) choose(row);
              }}
            >
              <option value="">Choose a connection</option>
              {usable.map((row) => (
                <option key={row.id} value={row.id}>
                  {row.name}
                </option>
              ))}
            </select>
          </label>

          {selected && (
            <>
              <label className="grid gap-1 text-sm">
                Tool name
                <Input
                  aria-label="Tool name"
                  value={toolName}
                  onChange={(event) => setToolName(event.target.value)}
                />
              </label>

              <fieldset className="grid gap-2">
                <legend className="text-sm font-medium">Actions</legend>
                {scopes.map((scope) => {
                  const klass = scopeClass(scope);
                  const disabled = klass === "destructive";
                  return (
                    <div
                      key={scope}
                      className="flex items-center justify-between gap-3 rounded-md border px-3 py-2"
                    >
                      <div className="grid gap-0.5">
                        <span className="font-mono text-[0.8125rem]">{scope}</span>
                        <span className="text-xs text-muted-foreground">
                          {klass === "read"
                            ? "Read access"
                            : klass === "write"
                              ? "Write access, off by default"
                              : "Destructive, not available yet"}
                        </span>
                      </div>
                      <Switch
                        aria-label={`Allow ${scope}`}
                        checked={allowed[scope] === true && !disabled}
                        disabled={disabled}
                        onCheckedChange={(on) => setAllowed((prev) => ({ ...prev, [scope]: on }))}
                      />
                    </div>
                  );
                })}
              </fieldset>

              {chosen.length > 0 && (
                <p className="flex items-center gap-2 text-sm">
                  Risk
                  <Badge variant={riskLabels[riskClass].variant}>
                    {riskLabels[riskClass].label}
                  </Badge>
                  <span className="text-muted-foreground">
                    {writeChosen ? "Includes write actions" : "Read only"}
                  </span>
                </p>
              )}
            </>
          )}

          <p className="flex items-center gap-2 text-xs text-muted-foreground">
            <Lock aria-hidden className="size-3.5 shrink-0" />
            Credentials stay in the broker and never enter the sandbox.
          </p>

          {error && (
            <p role="alert" className="text-sm text-danger">
              {error}
            </p>
          )}

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onDone()}>
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {mutation.isPending ? "Adding…" : "Add tool"}
            </Button>
          </div>
        </form>
      )}
    </>
  );
}
